// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

// M1 — Mermaid text generator (pure function, PRD §M1).
// Maps a graph agent's wire-shape `nodes` + `edges` (snake_case, GraphAgentModel)
// to a Mermaid `flowchart TD` string. Renderer-agnostic — no DOM, no I/O, no
// dependency on the `mermaid` library — so topology bugs are caught by fast unit
// tests without rendering anything.
//
// M1 contract:
//   - Sentinels `__start__` / `__end__` never appear raw; edges touching them are
//     remapped to stadium endpoint nodes (`input` / `output`, class `io`). No
//     sentinel edge → no endpoint node.
//   - `node_type` → class via a closed table (validator/gating → `gate`, sentinels
//     → `io`, everything else and unknown → `llm`).
//   - Conditional edge (non-null `routing_key`) → labeled arrow; null → plain arrow.
//   - Node ids are sanitized to a Mermaid-safe alphabet; display labels carry the
//     original `name` with syntax-breaking chars escaped (ebiose-core Python
//     serializer's `#91;` / `#93;` bracket escaping is the reference).
//   - Deterministic: same input always yields the same definition.
//
// Visual class tokens come from the design handoff prototype — no invented values.

const START_SENTINEL = "__start__"
const END_SENTINEL = "__end__"

// Exported so the renderer can recognise a clicked endpoint and the inspector can
// map the selection back to the agent-level input/output schema. Endpoints are not
// concrete NodeModels — they stand in for the `__start__` / `__end__` sentinels.
// Already Mermaid-id-safe, so a rendered endpoint's Mermaid id IS this literal.
export const INPUT_ID = "input"
export const OUTPUT_ID = "output"

// Closed set: any node_type listed here maps to `gate`; everything else
// (including unknown types) defaults to `llm`. Sentinels handled separately (`io`).
const GATE_NODE_TYPES = new Set<string>([
  "ValidatorNode",
  "PydanticValidatorNode",
  "JSONSchemaValidatorNode",
  "RegExNode",
])

type MermaidClass = "llm" | "gate" | "io"

// Exported as the single owner of the gate-or-LLM distinction; callers must not
// copy the underlying GATE_NODE_TYPES table.
export function isGateNodeType(nodeType: string): boolean {
  return GATE_NODE_TYPES.has(nodeType)
}

// Unknown types default to `llm`.
function classForNodeType(nodeType: string): MermaidClass {
  return isGateNodeType(nodeType) ? "gate" : "llm"
}

// Sanitizes to `[A-Za-z0-9_]`; a leading digit gets an `n_` prefix so ids never
// start with a number. Exported so renderers resolve an SVG element back to the
// original node identity using the exact same mapping the definition was built with.
export function sanitizeId(rawId: string): string {
  let safe = rawId.replace(/[^A-Za-z0-9_]/g, "_")
  if (safe === "" || /^[0-9]/.test(safe)) safe = `n_${safe}`
  return safe
}

// sanitizeId alone is not injective (`a-b` and `a_b` both sanitize to `a_b`), so
// colliding ids get a deterministic `_2`, `_3`, … suffix in input order. The
// synthetic endpoint ids are reserved upfront so a node named `input` / `output`
// can never collide with them. Exported so the renderer resolves clicked SVG nodes
// through the exact same map the definition was generated with.
export function buildMermaidIdMap(nodes: NodeModel[]): Map<string, string> {
  const taken = new Set<string>([INPUT_ID, OUTPUT_ID])
  const map = new Map<string, string>()
  for (const node of nodes) {
    let id = sanitizeId(node.id)
    if (taken.has(id)) {
      let n = 2
      while (taken.has(`${id}_${n}`)) n += 1
      id = `${id}_${n}`
    }
    taken.add(id)
    map.set(node.id, id)
  }
  return map
}

// `#NN;` entity form matches the ebiose-core Python serializer's bracket escaping
// (`#91;` / `#93;`); extended here to all characters that terminate a `[...]` label
// or inject Mermaid markup. The caller additionally wraps the result in quotes.
function escapeLabel(label: string): string {
  return label
    .replace(/"/g, "#quot;")
    .replace(/\[/g, "#91;")
    .replace(/\]/g, "#93;")
    .replace(/\{/g, "#123;")
    .replace(/\}/g, "#125;")
    .replace(/\(/g, "#40;")
    .replace(/\)/g, "#41;")
}

// No surrounding brackets to break, but pipes / quotes can still inject syntax.
function escapeEdgeLabel(label: string): string {
  return label.replace(/"/g, "#quot;").replace(/\|/g, "#124;")
}

interface ResolvedEndpoint {
  id: string
  isEndpoint: boolean
}

export function generateMermaidFlowchart(
  nodes: NodeModel[],
  edges: EdgeModel[],
): string {
  // Collision-free map raw node id → Mermaid id for every concrete
  // (non-sentinel) node.
  const idMap = buildMermaidIdMap(nodes)

  // Resolve a wire id (possibly a sentinel) to the Mermaid id used in
  // arrows. Sentinels resolve to the endpoint ids.
  function resolve(wireId: string): ResolvedEndpoint {
    if (wireId === START_SENTINEL) return { id: INPUT_ID, isEndpoint: true }
    if (wireId === END_SENTINEL) return { id: OUTPUT_ID, isEndpoint: true }
    return { id: idMap.get(wireId) ?? sanitizeId(wireId), isEndpoint: false }
  }

  // Which endpoints actually occur in the edge data — conditional
  // remapping: no sentinel edge → no endpoint node.
  let needsInput = false
  let needsOutput = false
  for (const edge of edges) {
    if (edge.source_id === START_SENTINEL || edge.target_id === START_SENTINEL)
      needsInput = true
    if (edge.source_id === END_SENTINEL || edge.target_id === END_SENTINEL)
      needsOutput = true
  }

  const lines: string[] = ["flowchart TD"]

  // Endpoint declarations (stadium shape + io class).
  if (needsInput) lines.push(`  ${INPUT_ID}([${INPUT_ID}]):::io`)
  if (needsOutput) lines.push(`  ${OUTPUT_ID}([${OUTPUT_ID}]):::io`)

  // Concrete node declarations — exactly one per node, in input order.
  for (const node of nodes) {
    const id = idMap.get(node.id)!
    const cls = classForNodeType(node.node_type)
    const label = escapeLabel(node.name)
    lines.push(`  ${id}["${label}"]:::${cls}`)
  }

  // Edges — one line each, labeled iff routing_key is non-null.
  for (const edge of edges) {
    const from = resolve(edge.source_id).id
    const to = resolve(edge.target_id).id
    if (edge.routing_key != null && edge.routing_key !== "") {
      const label = escapeEdgeLabel(edge.routing_key)
      lines.push(`  ${from} -- ${label} --> ${to}`)
    } else {
      lines.push(`  ${from} --> ${to}`)
    }
  }

  // classDef lines — Lagon design-system tokens (visual spec of record):
  // LLM nodes in mint/teal, gate nodes in lineage violet, endpoints as
  // muted in/out chips. Mirrors the mockup's graph legend.
  lines.push(
    "  classDef llm fill:#0B2A30,stroke:#2E6E72,stroke-width:1.5px,color:#D9F2EA;",
  )
  lines.push(
    "  classDef gate fill:#0B2A30,stroke:#9D7BFF,stroke-width:1.5px,color:#D9F2EA;",
  )
  lines.push("  classDef io fill:#0A2024,stroke:#3A5C58,color:#A9CFC6;")

  return lines.join("\n")
}
